import type { ChangeEvent } from 'react';

import type { InputType, InputValue } from './types';

import { fromDateInputValue, toDateInputValue } from '../../utils/formatDate';

/** Intermediate number shapes while typing: "", "-", ".", "-.", "1.", "-1.5", … */
const PARTIAL_NUMBER_PATTERN = /^-?\d*\.?\d*$/;

/**
 * `type="number"` hides in-progress text: `value` is "" for "-" and ".", and
 * drops a trailing dot, so the visible text is tracked here instead. `value`
 * records what the element read when the draft was written, which is what
 * distinguishes a browser-sanitized draft from an externally cleared field.
 */
const numberInputDrafts = new WeakMap<
  HTMLInputElement,
  { text: string; value: string }
>();

/** Checks if min/max prop (i.e. bound) is a valid date and if so, returns
 * the date input value */
export const toDateBoundAttribute = (
  type: InputType,
  bound: unknown,
): string | undefined => {
  if (type !== 'date' && type !== 'datetime-local') {
    return undefined;
  }
  if (!(bound instanceof Date) || Number.isNaN(bound.getTime())) {
    return undefined;
  }
  return toDateInputValue(bound, type);
};

export const toDisplayValue = (
  value: InputValue,
  type: InputType,
): string | number => {
  if (value === null) {
    return '';
  }
  if (value instanceof Date) {
    return toDateInputValue(
      value,
      type === 'datetime-local' ? 'datetime-local' : 'date',
    );
  }
  return value;
};

export const fromInputEvent = (
  e: ChangeEvent<HTMLInputElement>,
  type: InputType,
): InputValue => {
  if (type === 'number') {
    const updatedValue = e.target.valueAsNumber;
    return Number.isNaN(updatedValue) ? null : updatedValue;
  }
  if (type === 'date' || type === 'datetime-local') {
    return fromDateInputValue(e.target.value, type);
  }
  return e.target.value;
};

export const isAllowedNumberInputValue = (value: string) =>
  PARTIAL_NUMBER_PATTERN.test(value);

/** Digit-free shapes a browser reports as "": "-", ".", "-." */
const DIGITLESS_NUMBER_PATTERN = /^-?\.?$/;

function writeNumberInputDraft(input: HTMLInputElement, text: string) {
  numberInputDrafts.set(input, { text, value: input.value });
}

function readNumberInputDraft(input: HTMLInputElement) {
  const { value, valueAsNumber } = input;
  const tracked = numberInputDrafts.get(input);

  if (!tracked) {
    return value;
  }

  if (value !== '') {
    // A trailing dot is the one character the draft may legitimately hold on
    // top of `value` (Firefox reports "1" while the field shows "1.").
    const isCurrent =
      tracked.text === value || tracked.text.replace(/\.$/, '') === value;
    return isCurrent ? tracked.text : value;
  }

  // An empty `value` is ambiguous — the field may hold text the browser won't
  // parse, or may have been cleared by the consumer.
  if (!Number.isNaN(valueAsNumber)) {
    return tracked.text;
  }
  if (tracked.value !== '') {
    return '';
  }
  return DIGITLESS_NUMBER_PATTERN.test(tracked.text) ? tracked.text : '';
}

/** `type="number"` never exposes a caret, and `value` can lag the visible
 * draft — in both cases the edit is treated as happening at the end. */
function resolveSelection(input: HTMLInputElement, draft: string) {
  const { selectionEnd, selectionStart, value } = input;
  if (value !== draft || selectionStart == null || selectionEnd == null) {
    return { end: draft.length, start: draft.length };
  }
  return { end: selectionEnd, start: selectionStart };
}

function applyDelete(
  draft: string,
  inputType: string,
  start: number,
  end: number,
) {
  if (start !== end) {
    return draft.slice(0, start) + draft.slice(end);
  }
  if (inputType === 'deleteContentBackward' && start > 0) {
    return draft.slice(0, start - 1) + draft.slice(start);
  }
  if (inputType === 'deleteContentForward') {
    return draft.slice(0, start) + draft.slice(start + 1);
  }
  return draft;
}

/** Native `beforeinput` handler to block non-numeric characters in number input */
export const handleNumberBeforeInput = (event: InputEvent) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  const draft = readNumberInputDraft(input);
  const { end, start } = resolveSelection(input, draft);

  if (!event.data) {
    writeNumberInputDraft(
      input,
      applyDelete(draft, event.inputType, start, end),
    );
    return;
  }

  const next = draft.slice(0, start) + event.data + draft.slice(end);

  if (!isAllowedNumberInputValue(next)) {
    event.preventDefault();
    return;
  }

  writeNumberInputDraft(input, next);
};
